import { PaymentRepository } from './paymentRepository';
// import { verifyPayment } from '@/lib/payment/portone';
import type { Subscription, SubscriptionPlan, SubscriptionUsageStat, Payment } from '@/lib/payment/types';
import { issueBillingKey } from '@/lib/payment/portone';
import { logError } from '@/lib/sentry';

export class PaymentService {
  private repository: PaymentRepository;

  constructor() {
    this.repository = new PaymentRepository();
  }

  // 빌링키 등록 함수 추가
  async registerBillingKey(userId: string, billingKey: string): Promise<void> {
    try {
      // billing_key 테이블에 빌링키 정보 저장
      await this.repository.saveBillingKey(userId, billingKey);
    } catch (error) {
      logError('빌링키 저장 중 오류 발생:', error);
      throw new Error('빌링키 저장에 실패했습니다');
    }
  }

  // 구독 시작을 위한 사전 데이터 로드
  async prepareSubscription(userId: string, planId: string): Promise<{
    plan: SubscriptionPlan;
    currentSubscription: Subscription | null;
  }> {
    // 1. 활성 구독 확인
    const currentSubscription = await this.repository.getCurrentSubscription(userId);
    // 현재 구독이 있고, 대상 플랜과 동일하며, 현재 구독이 체험판이 아닌 경우에만 에러 발생
    if (currentSubscription &&
        currentSubscription.subscription_plan_id === planId &&
        !currentSubscription.is_trial) {
      throw new Error('현재 이용 중인 플랜과 동일한 플랜입니다.');
    }

    // 2. 플랜 정보 조회
    const plan = await this.repository.getSubscriptionPlan(planId);
    if (!plan) {
      throw new Error('구독 플랜을 찾을 수 없습니다.');
    }

    return { plan, currentSubscription };
  }

  // 결제 정보 생성 함수 추출
  async createPayment(
    userId: string,
    planId: string,
    paymentId: string,
    amount: number,
    status: Payment['status'] = 'PAID',
    paidAt?: Date,
    pgTransactionId?: string,
    type: Payment['type'] = 'subscription'
  ): Promise<Payment> {
    return await this.repository.createPayment({
      user_id: userId,
      subscription_plan_id: planId,
      payment_id: paymentId,
      amount: amount,
      status: status,
      paid_at: paidAt,
      pg_transaction_id: pgTransactionId,
      type
    });
  }

  // 빌링키로 결제 처리 (callback.tsx에서 사용)
  async processBillingKeyPayment(
    userId: string,
    planId: string,
    billingKey: string
  ): Promise<string | null> { // 원래대로 paymentId 또는 null 반환
    try {
      // 1. 플랜 정보 조회
      // prepareSubscription은 planId에 해당하는 plan이 없으면 에러를 throw하므로 plan 객체는 항상 유효하다고 가정 가능.
      const { plan } = await this.prepareSubscription(userId, planId);

      // 2. 피추천인 첫 결제 90% 할인 체크
      let finalPrice = plan.price;
      let isReferralDiscount = false;
      
      try {
        const { supabase } = await import('../client');
        const { data: referralData } = await supabase
          .from('referral')
          .select('id, first_payment_at')
          .eq('referee_id', userId)
          .single();
        
        // 피추천인이고 아직 첫 결제 전이면 90% 할인
        if (referralData && !referralData.first_payment_at) {
          finalPrice = Math.round(plan.price * 0.1); // 90% 할인 (10%만 결제)
          isReferralDiscount = true;
          console.log(`피추천인 첫 결제 90% 할인 적용: ${plan.price}원 → ${finalPrice}원`);
        }
      } catch (referralError) {
        // referral 조회 실패해도 정상 결제 진행
        console.log('referral 조회 실패 (할인 미적용):', referralError);
      }

      // 3. 결제 ID 생성
      const paymentId = `payment_${userId}_${Date.now()}`;

      // 4. 빌링키로 정기결제 요청 (Edge Function 호출)
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/billing-payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customerKey: userId,
          billingKey: billingKey,
          amount: finalPrice,
          paymentId: paymentId,
          orderName: isReferralDiscount ? `${plan.name} 구독 (친구 초대 90% 할인)` : `${plan.name} 구독`,
        }),
      });

      if (!response.ok) {
        // 응답 데이터 파싱
        let errorData: any = {};
        try {
          errorData = await response.json();
        } catch (e) {
          logError('결제 응답 파싱 실패:', e);
        }

        // 결제 실패 시 UNPAID 상태로 기록
        await this.createPayment(userId, planId, paymentId, finalPrice, 'UNPAID', undefined, undefined, 'subscription');

        // 에러 타입과 메시지를 활용하여 상세 에러 생성
        const errorType = errorData.errorType || 'UNKNOWN_ERROR';
        const errorMessage = errorData.message || '결제 요청에 실패했습니다';
        const pgCode = errorData.pgCode || '';
        const pgMessage = errorData.pgMessage || '';

        // 상세 로깅
        logError('결제 실패:', {
          errorType,
          errorMessage,
          pgCode,
          pgMessage,
          userId,
          planId,
          paymentId
        });

        // 사용자에게 보여줄 메시지: pgMessage가 있으면 우선 사용 (더 친절한 메시지)
        const userFacingMessage = pgMessage || errorMessage;
        throw new Error(userFacingMessage);
      }

      const paymentResult = await response.json();
      console.log("결제 결과:", paymentResult);

      // 결제 결과에서 필드 직접 사용 (표준화된 응답 형식)
      const paidAt = paymentResult.paidAt;
      const pgTxId = paymentResult.pgTxId;

      console.log("결제 시간과 거래 ID:", { paidAt, pgTxId });

      // 5. 결제 정보 저장 (pg_transaction_id와 paid_at 정보 추가)
      await this.createPayment(
        userId,
        planId,
        paymentId,
        finalPrice,
        'PAID',
        paidAt ? new Date(paidAt) : undefined,
        pgTxId,
        'subscription'
      );

      return paymentId; // paymentId만 반환
    } catch (error) {
      logError('빌링키 결제 중 오류 발생:', error);
      throw error; // UI까지 에러 전파
    }
  }

  // 빌링키와 구독 처리 통합 메서드 (callback.tsx에서 사용)
  async processBillingKeyAndSubscribe(
    userId: string,
    billingKey: string,
    planId: string
  ): Promise<Subscription | null> {
    try {
      // 1. 결제 진행
      const paymentId = await this.processBillingKeyPayment(userId, planId, billingKey);

      if (!paymentId) {
        console.warn('processBillingKeyPayment failed or returned null.');
        return null;
      }
      
      // 2. 구독 활성화
      // activateSubscription은 이제 plan 객체를 받지 않음
      const newSubscription = await this.activateSubscription(userId, paymentId, planId);
      return newSubscription;
    } catch (error) {
      logError('빌링키 결제 및 구독 활성화 중 오류 발생:', error);
      throw error; // UI까지 에러 전파
    }
  }

  // 구독 시작 처리
  async startSubscription(
    userId: string,
    planId: string,
  ): Promise<Subscription | null> {
    try {
      // 1. 사전 데이터 로드
      // targetPlan 변수는 activateSubscription에 전달되지 않으므로 여기서 plan 객체를 받을 필요는 없음.
      // prepareSubscription은 유효성 검사 목적으로 호출.
      await this.prepareSubscription(userId, planId);

      // 2. 빌링키 발급 요청 - 이 때 planId를 쿼리 파라미터로 추가
      const redirectUrl = new URL(window.location.origin + '/payment/callback');
      redirectUrl.searchParams.append('planId', planId); // planId를 쿼리 파라미터로 추가

      const billingKeyResponse = await issueBillingKey(userId, redirectUrl.toString());
      console.log("🔥 billingKeyResponse", billingKeyResponse)

      // 빌링키 발급에 실패한 경우
      if (!billingKeyResponse.billingKey) {
        // 사용자가 취소한 것으로 간주
        console.log('빌링키 발급 취소됨');
        // throw new Error('UserCancelError'); // UserCancelError를 throw하여 호출부에서 일관되게 처리
        return null; // 또는 null 반환하여 호출부에서 구분
      }

      // 3. 빌링키 저장
      await this.registerBillingKey(userId, billingKeyResponse.billingKey);

      // 4. 빌링키로 결제 처리
      const paymentId = await this.processBillingKeyPayment(userId, planId, billingKeyResponse.billingKey);
      
      // 5. 구독 활성화 및 생성된 구독 객체 받기
      if (paymentId) {
        // activateSubscription은 이제 plan 객체를 받지 않음
        const newSubscription = await this.activateSubscription(userId, paymentId, planId);
        return newSubscription;
      }
      return null; // 결제가 실패했거나 paymentId가 없는 경우
    } catch (error) {
      logError('구독 시작 중 오류 발생:', error);

      // 에러가 UserCancelError인 경우 그대로 전달
      if (error instanceof Error && error.name === 'UserCancelError') {
        throw error;
      }

      // 에러 객체에 타입 정보 추가 (UI에서 사용자 취소와 실제 오류를 구분하기 위함)
      if (error instanceof Error &&
        (error.message.includes('사용자가 결제를 취소') ||
          error.message.includes('창이 닫혔습니다') ||
          error.message.includes('PAY_PROCESS_CANCELED'))) {
        const userCancelError = new Error('사용자가 결제를 취소했습니다');
        userCancelError.name = 'UserCancelError';
        throw userCancelError;
      }

      throw error;
    }
  }

  // 결제 완료 후 구독 활성화
  async activateSubscription(userId: string, paymentId: string, planId: string): Promise<Subscription> { // plan 인자 제거
    try {
      // 2-1. 기존 활성 구독이 있는지 확인하고 있으면 만료(expired) 처리
      const currentActiveSubscription = await this.repository.getCurrentSubscription(userId);
      if (currentActiveSubscription) {
        console.log('기존 active 구독 만료 처리:', currentActiveSubscription.id);
        await this.repository.updateSubscriptionStatus(
          currentActiveSubscription.id,
          'expired',
          false,
          null // downgrade_to 필드를 null로 초기화
        );
      }

      // 추가: 기존 past_due 구독이 있는지 확인하고 있으면 만료(expired) 처리
      const pastDueSubscription = await this.repository.getPastDueSubscription(userId);
      if (pastDueSubscription) {
        console.log('기존 past_due 구독 만료 처리:', pastDueSubscription.id);
        await this.repository.updateSubscriptionStatus(
          pastDueSubscription.id,
          'expired',
          false,
          null // downgrade_to 필드를 null로 초기화
        );
      }

      // 추가: 피추천인의 첫 결제인 경우 referral.first_payment_at 업데이트
      // (이 사용자가 피추천인으로 등록되어 있고, 아직 first_payment_at이 없는 경우)
      try {
        const { supabase } = await import('../client');

        const { data: referralData } = await supabase
          .from('referral')
          .select('id, first_payment_at')
          .eq('referee_id', userId)
          .single();

        if (referralData && !referralData.first_payment_at) {
          const { error: updateError } = await supabase
            .from('referral')
            .update({ first_payment_at: new Date().toISOString() })
            .eq('id', referralData.id);
          
          if (updateError) {
            console.warn('referral first_payment_at 업데이트 실패:', updateError);
          } else {
            console.log('피추천인 첫 결제 시간 업데이트:', userId);
          }
        }
      } catch (referralError) {
        // referral 업데이트 실패해도 구독 활성화는 계속 진행
        console.warn('referral first_payment_at 업데이트 실패 (무시됨):', referralError);
      }

      // 3. 새 구독 정보 생성 (가입일 기준 30일 사용)
      const now = new Date();
      const periodStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0); // 당일 00:00:00.000
      const periodEnd = new Date(periodStart); // periodStart를 기준으로 계산
      periodEnd.setDate(periodStart.getDate() + 29); // 29일을 더해서 정확히 30일 사용 가능하도록 함
      periodEnd.setHours(23, 59, 59, 999);
      periodEnd.setMilliseconds(periodEnd.getMilliseconds() - 1); // 마지막 밀리초 제외

      const subscription = await this.repository.createSubscription({
        user_id: userId,
        subscription_plan_id: planId,
        payment_id: paymentId,
        status: 'active',
        period_start: periodStart,
        period_end: periodEnd,
        cancel_at_period_end: false,
        downgrade_to: null, // 명시적으로 downgrade_to 설정
        is_trial: false
      });

      // 4. 사용량 통계 초기화
      await this.repository.createOrUpdateUsageStat({
        user_id: userId,
        subscription_id: subscription.id,
        subscription_plan_id: planId,
        used_count: 0,
        copy_used_count: 0,
        period_start: periodStart,
        period_end: periodEnd,
        is_trial: false // 새 유료 구독이므로 is_trial은 false
      });
      
      // 생성된 구독 객체 반환
      return subscription;

    } catch (error) {
      logError('구독 활성화 중 오류 발생:', error);
      throw error;
    }
  }

  // 현재 구독 정보 조회
  async getCurrentSubscription(userId: string): Promise<Subscription | null> {
    return await this.repository.getCurrentSubscription(userId);
  }

  // past_due 상태의 구독 조회
  async getPastDueSubscription(userId: string): Promise<Subscription | null> {
    return await this.repository.getPastDueSubscription(userId);
  }

  // 플랜 ID로 구독 플랜 정보 조회
  async getSubscriptionPlan(planId: string): Promise<SubscriptionPlan | null> {
    return await this.repository.getSubscriptionPlan(planId);
  }

  // 현재 구독 플랜 조회
  async getCurrentPlan(userId: string): Promise<SubscriptionPlan | null> {
    try {
      const subscription = await this.getCurrentSubscription(userId);

      if (!subscription) {
        return await this.repository.getSubscriptionPlan('free');
      }

      // Supabase에서는 스네이크 케이스로 반환되므로 subscription_plan_id로 접근
      const planId = (subscription as any).subscription_plan_id;
      if (!planId) {
        return await this.repository.getSubscriptionPlan('free');
      }

      // 현재 구독 중인 플랜 정보 반환
      const plan = await this.repository.getSubscriptionPlan(planId);

      if (!plan) {
        return await this.repository.getSubscriptionPlan('free');
      }

      return plan;
    } catch (error) {
      logError('구독 플랜 조회 중 오류 발생:', error);
      return await this.repository.getSubscriptionPlan('free');
    }
  }

  // 모든 구독 플랜 조회
  async getAllPlans(): Promise<SubscriptionPlan[]> {
    return await this.repository.getAllSubscriptionPlans();
  }

  // 현재 사용량 통계 조회
  async getCurrentUsageStat(userId: string, subscriptionId: number): Promise<SubscriptionUsageStat | null> {
    return await this.repository.getCurrentUsageStat(userId, subscriptionId);
  }

  // 사용량 증가
  async incrementUsage(userId: string): Promise<{ success: boolean, reason?: string }> {
    console.log("🔥 incrementUsage 호출:", { userId });
    try {
      // 1. 현재 활성 구독 조회
      const subscription = await this.repository.getCurrentSubscription(userId);

      console.log("🔥 subscription asdf", subscription)
      if (!subscription) {
        // 구독이 없는 경우, 사용량 증가를 허용하지 않고 실패로 처리 (혹은 다른 정책 적용 가능)
        logError('사용량 증가 시도: 활성 구독 없음', { userId });
        return { success: false, reason: 'no_active_subscription' };
      }

      // 2. 현재 사용량 통계 조회
      const usageStat = await this.repository.getCurrentUsageStat(userId, subscription.id);
      if (!usageStat) {
         // 사용량 통계가 없는 경우, 새로 생성하거나 오류 처리
        logError('사용량 증가 시도: 사용량 통계 없음', { userId, subscriptionId: subscription.id });
        // 일단 실패로 처리. 필요시 여기서 createOrUpdateUsageStat 호출 고려
        return { success: false, reason: 'no_usage_stat' };
      }

      // 3. 플랜 정보 조회
      const plan = await this.repository.getSubscriptionPlan(subscription.subscription_plan_id);
      if (!plan) {
        logError('사용량 증가 시도: 구독 플랜 정보 없음', { userId, planId: subscription.subscription_plan_id });
        return { success: false, reason: 'no_plan_info' };
      }

      // 4. 사용량 제한 체크 (무제한 플랜이 아닌 경우)
      if (plan.usage_limit !== null && usageStat.used_count >= plan.usage_limit) {
        console.log("🔥 사용량 한도 도달:", { userId, planId: plan.id, used: usageStat.used_count, limit: plan.usage_limit });
        return { success: false, reason: 'usage_limit_exceeded' };
      }

      console.log("🔥 사용량 제한 체크 완료:", { usageStat });

      // 5. 사용량 증가
      await this.repository.incrementUsageCount(usageStat.id);
      return { success: true };
    } catch (error) {
      logError('사용량 증가 중 예상치 못한 오류 발생:', error);
      // 일반적인 오류는 그대로 throw 하거나, 혹은 { success: false, reason: 'unknown_error' } 등으로 처리 가능
      throw error; // 기존 로직 유지 또는 변경
    }
  }

  // 복사 사용량 증가
  async incrementCopyUsage(userId: string): Promise<{ success: boolean, reason?: string }> {
    console.log("🔥 incrementCopyUsage 호출:", { userId });
    try {
      // 1. 현재 활성 구독 조회
      const subscription = await this.repository.getCurrentSubscription(userId);

      if (!subscription) {
        logError('복사 사용량 증가 시도: 활성 구독 없음', { userId });
        return { success: false, reason: 'no_active_subscription' };
      }

      // 2. 현재 사용량 통계 조회
      const usageStat = await this.repository.getCurrentUsageStat(userId, subscription.id);
      if (!usageStat) {
        logError('복사 사용량 증가 시도: 사용량 통계 없음', { userId, subscriptionId: subscription.id });
        return { success: false, reason: 'no_usage_stat' };
      }

      // 3. 플랜 정보 조회
      const plan = await this.repository.getSubscriptionPlan(subscription.subscription_plan_id);
      if (!plan) {
        logError('복사 사용량 증가 시도: 구독 플랜 정보 없음', { userId, planId: subscription.subscription_plan_id });
        return { success: false, reason: 'no_plan_info' };
      }

      // 4. 복사 사용량 제한 체크 (무제한 플랜이 아닌 경우)
      if (plan.copy_usage_limit !== null && usageStat.copy_used_count >= plan.copy_usage_limit) {
        console.log("🔥 복사 사용량 한도 도달:", { userId, planId: plan.id, used: usageStat.copy_used_count, limit: plan.copy_usage_limit });
        return { success: false, reason: 'copy_usage_limit_exceeded' };
      }

      // 5. 복사 사용량 증가
      await this.repository.incrementCopyUsageCount(usageStat.id);
      return { success: true };
    } catch (error) {
      logError('복사 사용량 증가 중 예상치 못한 오류 발생:', error);
      throw error;
    }
  }

  // 무료 프리미엄 체험판 구독 생성 (회원가입 시 호출)
  async createFreePremiumTrialSubscription(userId: string): Promise<void> {
    try {
      const now = new Date();
      
      // 현재 날짜의 시작 (00:00:00)을 periodStart로 설정
      const periodStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      
      // periodStart로부터 6일 후의 종료일 23:59:59.999를 periodEnd로 설정 (총 7일)
      const periodEnd = new Date(periodStart);
      periodEnd.setDate(periodStart.getDate() + 6);
      periodEnd.setHours(23, 59, 59, 999);

      console.log(`무료 프리미엄 체험판 구독 생성 - 사용자: ${userId}, 기간: ${periodStart.toLocaleDateString('ko-KR')} ~ ${periodEnd.toLocaleDateString('ko-KR')} (KST)`);

      // 1. 무료 프리미엄 체험판 구독 정보 생성
      const subscription = await this.repository.createSubscription({
        user_id: userId,
        subscription_plan_id: 'premium',
        payment_id: null,
        status: 'active',
        period_start: periodStart,
        period_end: periodEnd,
        cancel_at_period_end: false,
        downgrade_to: null, // 명시적으로 downgrade_to 설정
        is_trial: true
      });

      // 2. 사용량 통계 초기화
      await this.repository.createOrUpdateUsageStat({
        user_id: userId,
        subscription_id: subscription.id,
        subscription_plan_id: 'premium',
        used_count: 0,
        copy_used_count: 0,
        period_start: periodStart,
        period_end: periodEnd,
        is_trial: true
      });
    } catch (error) {
      logError('무료 프리미엄 체험판 구독 생성 중 오류 발생:', error);
      throw error;
    }
  }

  // 스탠다드 -> 프리미엄 업그레이드 처리 함수 추가
  async upgradeSubscription(userId: string, currentPlanId: string, targetPlanId: string): Promise<string> {
    if (currentPlanId !== 'standard' || targetPlanId !== 'premium') {
      throw new Error('현재 스탠다드 플랜에서 프리미엄 플랜으로의 업그레이드만 지원합니다.');
    }

    try {
      // 1. 현재 활성 구독 정보 조회
      const currentSubscription = await this.repository.getCurrentSubscription(userId);
      if (!currentSubscription || currentSubscription.subscription_plan_id !== 'standard') {
        throw new Error('업그레이드할 스탠다드 구독이 없습니다.');
      }

      // 2. 현재 구독 기간 정보를 기반으로 남은 일수 계산
      const now = new Date();
      const periodEnd = new Date(currentSubscription.period_end);

      // 남은 일수 계산 (밀리초 -> 일)
      const remainingDays = Math.max(0, Math.ceil((periodEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));

      // 3. 플랜 정보 조회 (가격 차이 계산용)
      const standardPlan = await this.repository.getSubscriptionPlan('standard');
      const premiumPlan = await this.repository.getSubscriptionPlan('premium');

      if (!standardPlan || !premiumPlan) {
        throw new Error('구독 플랜 정보를 찾을 수 없습니다.');
      }

      // 4. 금액 차이에 따른 업그레이드 가격 계산
      // 프리미엄(24,900원) - 스탠다드(9,900원) = 15,000원 차액
      const priceDifference = premiumPlan.price - standardPlan.price;

      // 남은 기간에 따른 실제 차액 계산 (일할 계산)
      // 30일 기준으로 남은 일수만큼 금액 계산
      let upgradeAmount = Math.round((priceDifference * remainingDays) / 30);

      // 최소 결제 금액 설정 (100원 이하라면 100원으로 결제)
      upgradeAmount = Math.max(100, upgradeAmount);

      console.log('업그레이드 결제 정보:', {
        remainingDays,
        priceDifference,
        upgradeAmount
      });

      // 5. 결제 정보 생성
      const paymentId = `upgrade_${userId}_${Date.now()}`;

      // 6. 빌링키 조회
      const { data: billingKeyData, error: billingKeyError } = await this.repository.getBillingKey(userId);

      if (billingKeyError || !billingKeyData) {
        throw new Error('등록된 결제 카드 정보가 없습니다. 카드를 먼저 등록해주세요.');
      }

      // 7. 빌링키로 업그레이드 금액 결제 처리
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/billing-payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customerKey: userId,
          billingKey: billingKeyData.key,
          amount: upgradeAmount,
          paymentId: paymentId,
          orderName: `스탠다드 → 프리미엄 업그레이드`,
        }),
      });

      if (!response.ok) {
        // 응답 데이터 파싱
        let errorData: any = {};
        try {
          errorData = await response.json();
        } catch (e) {
          logError('결제 응답 파싱 실패:', e);
        }

        // 결제 실패 시 UNPAID 상태로 기록
        await this.createPayment(userId, 'premium', paymentId, upgradeAmount, 'UNPAID', undefined, undefined, 'upgrade');

        // 에러 타입과 메시지를 활용하여 상세 에러 생성
        const errorType = errorData.errorType || 'UNKNOWN_ERROR';
        const errorMessage = errorData.message || '업그레이드 결제에 실패했습니다';

        throw new Error(`${errorType}: ${errorMessage}`);
      }

      const paymentResult = await response.json();

      // 8. 결제 정보 저장 (표준화된 응답 형식 사용)
      const paidAt = paymentResult.paidAt;
      const pgTxId = paymentResult.pgTxId;

      await this.createPayment(
        userId,
        'premium',
        paymentId,
        upgradeAmount,
        'PAID',
        paidAt ? new Date(paidAt) : undefined,
        pgTxId,
        'upgrade'
      );

      // 9. 기존 구독 만료 처리
      await this.repository.updateSubscriptionStatus(
        currentSubscription.id,
        'expired',
        false,
        null // downgrade_to 필드를 null로 초기화
      );

      // 10. 새 프리미엄 구독 생성 (기간은 기존 구독과 동일하게 유지)
      const newSubscription = await this.repository.createSubscription({
        user_id: userId,
        subscription_plan_id: 'premium',
        payment_id: paymentId,
        status: 'active',
        period_start: new Date(currentSubscription.period_start),
        period_end: new Date(currentSubscription.period_end),
        cancel_at_period_end: false,
        downgrade_to: null, // 명시적으로 downgrade_to 설정
        is_trial: false
      });

      // 기존 사용량 통계가 없는 경우 새로 생성
      await this.repository.createOrUpdateUsageStat({
        user_id: userId,
        subscription_id: newSubscription.id,
        subscription_plan_id: 'premium',
        used_count: 0, // 사용량 0으로 초기화
        copy_used_count: 0, // 복사 사용량 0으로 초기화
        period_start: new Date(currentSubscription.period_start),
        period_end: new Date(currentSubscription.period_end),
        is_trial: false
      });

      return paymentId;
    } catch (error) {
      logError('구독 업그레이드 중 오류 발생:', error);
      throw error;
    }
  }

  // 구독 다운그레이드 예약 메서드 추가
  async scheduleDowngrade(userId: string, targetPlanId: string): Promise<void> {
    if (targetPlanId !== 'standard') {
      throw new Error('프리미엄 플랜에서 스탠다드 플랜으로의 다운그레이드만 지원합니다.');
    }

    try {
      // 1. 현재 활성 구독 정보 조회
      const currentSubscription = await this.repository.getCurrentSubscription(userId);
      if (!currentSubscription) {
        throw new Error('활성 구독을 찾을 수 없습니다.');
      }

      if (currentSubscription.subscription_plan_id !== 'premium' || currentSubscription.is_trial) {
        throw new Error('프리미엄 플랜에서만 다운그레이드를 예약할 수 있습니다.');
      }

      // 이미 다운그레이드가 예약된 경우
      if (currentSubscription.downgrade_to === 'standard') {
        throw new Error('이미 다운그레이드가 예약되어 있습니다.');
      }

      // 2. 구독 다운그레이드 예약 (downgrade_to 필드 설정)
      await this.repository.updateSubscriptionStatus(
        currentSubscription.id,
        'active',
        false, // cancel_at_period_end는 변경하지 않음 (구독 취소와 다운그레이드는 별개)
        'standard' // 다음 결제일부터 standard 플랜으로 변경
      );

      console.log(`구독 다운그레이드 예약 완료. 사용자: ${userId}, 다음 플랜: standard`);
    } catch (error) {
      logError('구독 다운그레이드 예약 중 오류 발생:', error);
      throw error;
    }
  }

  // 구독 다운그레이드 예약 취소 메서드 추가
  async cancelScheduledDowngrade(userId: string): Promise<void> {
    try {
      // 1. 현재 활성 구독 정보 조회
      const currentSubscription = await this.repository.getCurrentSubscription(userId);
      if (!currentSubscription) {
        throw new Error('활성 구독을 찾을 수 없습니다.');
      }

      // 다운그레이드가 예약되어 있지 않은 경우
      if (!currentSubscription.downgrade_to) {
        throw new Error('예약된 다운그레이드가 없습니다.');
      }

      // 2. 구독 다운그레이드 예약 취소 (downgrade_to 필드를 null로 초기화)
      await this.repository.updateSubscriptionStatus(
        currentSubscription.id,
        'active',
        currentSubscription.cancel_at_period_end, // 기존 취소 상태 유지
        null // 다운그레이드 예약 취소
      );

      console.log(`구독 다운그레이드 예약 취소 완료. 사용자: ${userId}`);
    } catch (error) {
      logError('구독 다운그레이드 예약 취소 중 오류 발생:', error);
      throw error;
    }
  }
} 